import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Home, Camera, FileText, Send, Upload, CheckCircle, Circle,
  Loader2, Save, Bed, Bath, User, Key, Image, Layout, X, Plus, Mail,
  Calendar, MessageSquare, Eye, Edit, Trash2, GripVertical, MapPin,
  Phone, Building2, Clock, FileImage, Printer, Download
} from "lucide-react";
import { API } from "@/App";

const priceQualifiers = [
  { value: "fixed", label: "Fixed Price" },
  { value: "offers_over", label: "Offers Over" },
  { value: "oiro", label: "Offers in Region Of" },
  { value: "guide_price", label: "Guide Price" },
  { value: "poa", label: "Price on Application" }
];

const statusColors = {
  draft: "bg-slate-500",
  pending_approval: "bg-amber-500",
  approved: "bg-blue-500",
  live: "bg-emerald-500",
  sstc: "bg-purple-500",
  sold: "bg-green-700",
  withdrawn: "bg-red-500"
};

const EPC_RATINGS = ["A", "B", "C", "D", "E", "F", "G"];

export const ListingDetailPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listing, setListing] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingFloorplans, setUploadingFloorplans] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Editable fields
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState("");
  const [priceQualifier, setPriceQualifier] = useState("fixed");
  const [photos, setPhotos] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  
  // EPC fields
  const [epcReference, setEpcReference] = useState("");
  const [epcRating, setEpcRating] = useState("");
  const [savingEpc, setSavingEpc] = useState(false);
  
  // Modal states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewingModal, setShowViewingModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingBrochure, setGeneratingBrochure] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [bookingViewing, setBookingViewing] = useState(false);
  
  // Viewing form
  const [viewingForm, setViewingForm] = useState({
    date: "",
    time: "",
    attendee_name: "",
    attendee_phone: "",
    attendee_email: "",
    notes: ""
  });
  
  // Email form
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    message: ""
  });

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/instructions/sales/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListing(res.data);
      setDescription(res.data.description || "");
      setFeatures(res.data.features || []);
      setPriceQualifier(res.data.price_qualifier || "fixed");
      setPhotos(res.data.photos || []);
      setFloorplans(res.data.floorplans || []);
      setEpcReference(res.data.epc_reference || "");
      setEpcRating(res.data.epc_rating || "");
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Failed to load listing");
      navigate("/sales");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/instructions/${listingId}`,
        { 
          description, 
          features, 
          price_qualifier: priceQualifier,
          photos,
          floorplans
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Details saved");
      fetchListing();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEpc = async () => {
    setSavingEpc(true);
    try {
      await axios.put(
        `${API}/instructions/${listingId}`,
        { 
          epc_reference: epcReference,
          epc_rating: epcRating
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("EPC details saved");
      fetchListing();
    } catch (error) {
      toast.error("Failed to save EPC details");
    } finally {
      setSavingEpc(false);
    }
  };

  const openEpcRegister = () => {
    // Open the gov.uk EPC register - if we have a reference, we can search
    const baseUrl = "https://find-energy-certificate.service.gov.uk";
    window.open(baseUrl, "_blank");
  };

  // Handle Delete Listing
  const handleDeleteListing = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/instructions/${listingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Listing deleted successfully");
      navigate("/sales");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete listing");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Handle Generate Brochure
  const handleGenerateBrochure = async () => {
    setGeneratingBrochure(true);
    try {
      const response = await axios.get(
        `${API}/instructions/${listingId}/brochure`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `brochure-${listing?.address_line1?.replace(/\s+/g, '-') || listingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Brochure downloaded");
    } catch (error) {
      // If brochure endpoint doesn't exist, show helpful message
      toast.error("Brochure generation is being set up. Please try again later.");
    } finally {
      setGeneratingBrochure(false);
    }
  };

  // Handle Book Viewing
  const handleBookViewing = async () => {
    if (!viewingForm.date || !viewingForm.time || !viewingForm.attendee_name) {
      toast.error("Please fill in date, time and attendee name");
      return;
    }
    
    setBookingViewing(true);
    try {
      await axios.post(
        `${API}/instructions/${listingId}/viewings`,
        {
          ...viewingForm,
          property_id: listingId,
          scheduled_datetime: `${viewingForm.date}T${viewingForm.time}:00`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Viewing booked successfully");
      setShowViewingModal(false);
      setViewingForm({
        date: "",
        time: "",
        attendee_name: "",
        attendee_phone: "",
        attendee_email: "",
        notes: ""
      });
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book viewing");
    } finally {
      setBookingViewing(false);
    }
  };

  // Handle Send Email
  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject) {
      toast.error("Please fill in recipient and subject");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/instructions/${listingId}/send-email`,
        {
          to: emailForm.to,
          subject: emailForm.subject,
          message: emailForm.message,
          property_address: listing?.address_line1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email sent successfully");
      setShowEmailModal(false);
      setEmailForm({ to: "", subject: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Initialize email form with property details
  const openEmailModal = () => {
    setEmailForm({
      to: "",
      subject: `Property Details: ${listing?.address_line1 || ""}`,
      message: `Dear Sir/Madam,\n\nPlease find below the details for the property at ${listing?.address_line1}, ${listing?.postcode}.\n\nAsking Price: £${listing?.asking_price?.toLocaleString() || "TBC"}\nBedrooms: ${listing?.bedrooms || "N/A"}\nBathrooms: ${listing?.bathrooms || "N/A"}\n\n${description || ""}\n\nPlease contact us for more information or to arrange a viewing.\n\nKind regards,\nGoodacres Sales & Lettings`
    });
    setShowEmailModal(true);
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleAction = async (action) => {
    try {
      await axios.post(
        `${API}/instructions/${listingId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Action completed: ${action.replace("-", " ")}`);
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  // Multi-file upload handler
  const uploadFiles = async (files, docType, setUploading) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    const newFiles = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        const res = await axios.post(
          `${API}/instructions/${listingId}/documents?doc_type=${docType}`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            } 
          }
        );
        successCount++;
        if (res.data.document) {
          newFiles.push(res.data.document);
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} ${docType}(s) uploaded`);
      fetchListing();
    } else {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  // Photo dropzone
  const onPhotoDrop = useCallback((acceptedFiles) => {
    uploadFiles(acceptedFiles, "photo", setUploadingPhotos);
  }, [listingId, token]);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    onDrop: onPhotoDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true
  });

  // Floorplan dropzone
  const onFloorplanDrop = useCallback((acceptedFiles) => {
    uploadFiles(acceptedFiles, "floorplan", setUploadingFloorplans);
  }, [listingId, token]);

  const { getRootProps: getFloorplanRootProps, getInputProps: getFloorplanInputProps, isDragActive: isFloorplanDragActive } = useDropzone({
    onDrop: onFloorplanDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.pdf'] },
    multiple: true
  });

  // Single file upload
  const handleSingleUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(
        `${API}/instructions/${listingId}/documents?doc_type=${docType}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      toast.success(`${docType.replace("_", " ")} uploaded`);
      fetchListing();
    } catch (error) {
      toast.error("Upload failed");
    }
  };

  // Drag and drop reorder for photos
  const handlePhotoDragEnd = async (result) => {
    if (!result.destination) return;
    
    const photosList = listing.documents?.filter(d => d.type === "photo") || [];
    const items = Array.from(photosList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update the documents array with new order
    const otherDocs = listing.documents?.filter(d => d.type !== "photo") || [];
    const newDocuments = [...items, ...otherDocs];
    
    // Save to backend
    try {
      await axios.put(
        `${API}/instructions/${listingId}`,
        { documents: newDocuments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Photo order saved");
      fetchListing();
    } catch (error) {
      toast.error("Failed to save order");
    }
  };

  const handleDeletePhoto = async (index) => {
    const photosList = listing.documents?.filter(d => d.type === "photo") || [];
    const newPhotos = photosList.filter((_, i) => i !== index);
    const otherDocs = listing.documents?.filter(d => d.type !== "photo") || [];
    const newDocuments = [...newPhotos, ...otherDocs];
    
    try {
      await axios.put(
        `${API}/instructions/${listingId}`,
        { documents: newDocuments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Photo deleted");
      fetchListing();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p>Listing not found</p>
      </div>
    );
  }

  const photosList = listing.documents?.filter(d => d.type === "photo") || [];
  const floorplansList = listing.documents?.filter(d => d.type === "floorplan") || [];
  const hasContract = listing.documents?.some(d => d.type === "contract");
  const hasId = listing.documents?.some(d => d.type === "id_document");
  const mainPhoto = photosList[0];

  const getPriceDisplay = () => {
    const price = listing.asking_price || 0;
    const qualifier = listing.price_qualifier || priceQualifier;
    const label = priceQualifiers.find(p => p.value === qualifier)?.label || "";
    
    if (qualifier === "fixed" || qualifier === "poa") {
      return qualifier === "poa" ? "Price on Application" : `£${price.toLocaleString()}`;
    }
    return `£${price.toLocaleString()} (${label})`;
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top Navigation Bar */}
      <header className="bg-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/sales")} className="text-white hover:bg-slate-600">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sales
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span>Ref: {listing.property_ref}</span>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b px-4 py-2 text-sm text-slate-600">
        <span className="hover:text-blue-600 cursor-pointer" onClick={() => navigate("/")}>Home</span>
        <span className="mx-2">›</span>
        <span className="hover:text-blue-600 cursor-pointer" onClick={() => navigate("/sales")}>Sales</span>
        <span className="mx-2">›</span>
        <span className="hover:text-blue-600 cursor-pointer" onClick={() => navigate("/sales")}>Properties</span>
        <span className="mx-2">›</span>
        <span>{listing.full_address}</span>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Main Content */}
          <div className="col-span-12 lg:col-span-9">
            {/* Property Header Card */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {/* Main Photo */}
                  <div className="w-48 h-36 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                    {mainPhoto ? (
                      <img 
                        src={`${API}/uploads/instructions/${listingId}/${mainPhoto.filename}`} 
                        alt="Property"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                  </div>

                  {/* Property Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h1 className="text-xl font-bold text-slate-800">
                          {listing.full_address} ({listing.property_ref})
                        </h1>
                        <p className="text-2xl font-bold text-emerald-600 mt-1">
                          {getPriceDisplay()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusColors[listing.status]} text-white`}>
                          {listing.status?.toUpperCase().replace("_", " ")}
                        </Badge>
                        <Badge variant="outline">Kempston Office</Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {listing.applicant_name || "No vendor"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        {listing.property_type?.replace("_", " ") || "House"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        {listing.bedrooms || 0} bedrooms
                      </span>
                    </div>

                    <p className="text-sm text-slate-600 line-clamp-2">
                      {description || "No description added yet. Click 'Marketing Details' to add one."}
                    </p>

                    <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Camera className="w-4 h-4" />
                        {photosList.length} photos uploaded
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleGenerateBrochure}
                        disabled={generatingBrochure}
                        data-testid="brochure-btn"
                      >
                        {generatingBrochure ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        Brochure
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={openEmailModal}
                        data-testid="email-btn"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                      <div className="ml-auto flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setShowDeleteDialog(true)}
                          data-testid="delete-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setActiveTab("details")}
                          data-testid="edit-btn"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs Section */}
            <div className="grid grid-cols-2 gap-6">
              {/* Viewings */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Viewings</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowViewingModal(true)}
                      data-testid="add-viewing-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="bg-purple-100 text-purple-800 text-center py-2 rounded font-medium">
                      Today
                    </div>
                    {listing.viewings && listing.viewings.length > 0 ? (
                      listing.viewings.map((v, idx) => (
                        <div key={idx} className="text-sm border-b pb-2">
                          <p className="font-medium">{v.attendee_name}</p>
                          <p className="text-slate-500">{new Date(v.scheduled_datetime).toLocaleString()}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 flex items-center gap-2 py-2">
                        <Clock className="w-4 h-4" />
                        There are no viewings booked
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Latest Correspondence */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Latest Correspondence</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {listing.draft_sent_at && (
                      <div className="text-sm border-b pb-2">
                        <p className="text-slate-500">{new Date(listing.draft_sent_at).toLocaleString()}</p>
                        <p>Email: Details sent to vendor for approval</p>
                      </div>
                    )}
                    <p className="text-sm text-slate-500">No recent correspondence</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Workflow Progress */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base">Listing Workflow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  {/* Step 1 */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${listing.draft_approved ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {listing.draft_approved ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Mail className="w-5 h-5 text-slate-400" />}
                    </div>
                    <p className="text-xs mt-1 text-center">Seller Approved</p>
                    {!listing.draft_approved && (
                      <Button size="sm" variant="link" className="text-xs p-0 h-auto" onClick={() => handleAction("send-draft")}>
                        Send to Seller
                      </Button>
                    )}
                  </div>
                  <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
                  {/* Step 2 */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasContract || listing.contract_signed ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {hasContract || listing.contract_signed ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <FileText className="w-5 h-5 text-slate-400" />}
                    </div>
                    <p className="text-xs mt-1 text-center">Contract</p>
                    {!hasContract && !listing.contract_signed && (
                      <label>
                        <input type="file" className="hidden" onChange={(e) => handleSingleUpload(e, "contract")} />
                        <Button size="sm" variant="link" className="text-xs p-0 h-auto" asChild><span>Upload</span></Button>
                      </label>
                    )}
                  </div>
                  <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
                  {/* Step 3 */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasId || listing.ids_uploaded ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {hasId || listing.ids_uploaded ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <User className="w-5 h-5 text-slate-400" />}
                    </div>
                    <p className="text-xs mt-1 text-center">ID Verified</p>
                    {!hasId && !listing.ids_uploaded && (
                      <label>
                        <input type="file" className="hidden" onChange={(e) => handleSingleUpload(e, "id_document")} />
                        <Button size="sm" variant="link" className="text-xs p-0 h-auto" asChild><span>Upload</span></Button>
                      </label>
                    )}
                  </div>
                  <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
                  {/* Step 4 */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${listing.portal_live ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {listing.portal_live ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Upload className="w-5 h-5 text-slate-400" />}
                    </div>
                    <p className="text-xs mt-1 text-center">Set for Sale</p>
                    {(hasContract || listing.contract_signed) && (hasId || listing.ids_uploaded) && !listing.portal_live && (
                      <Button size="sm" variant="link" className="text-xs p-0 h-auto text-emerald-600" onClick={() => handleAction("go-live")}>
                        Go Live
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Property Menu */}
          <div className="col-span-12 lg:col-span-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-purple-600">Property menu</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="divide-y">
                  <button 
                    onClick={() => setActiveTab("dashboard")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "dashboard" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Dashboard
                  </button>
                  <button 
                    onClick={() => setActiveTab("details")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "details" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Property Details
                  </button>
                  <button 
                    onClick={() => setActiveTab("marketing")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "marketing" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Marketing Details
                  </button>
                  <button 
                    onClick={() => setActiveTab("photos")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "photos" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Photos
                  </button>
                  <button 
                    onClick={() => setActiveTab("floorplans")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "floorplans" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Floorplans
                  </button>
                  <button 
                    onClick={() => setActiveTab("features")}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeTab === "features" ? "text-purple-600 font-medium" : ""}`}
                  >
                    Features
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50">
                    Keys & Security Codes
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50">
                    Documents
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50">
                    Letters & Emails
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50">
                    Tasks
                  </button>
                </nav>
              </CardContent>
            </Card>

            {/* Price Qualifier */}
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Price Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="text-xs">Price Qualifier</Label>
                <Select value={priceQualifier} onValueChange={setPriceQualifier}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceQualifiers.map(q => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="w-full mt-2" onClick={handleSaveDetails} disabled={saving}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tab Content Panels */}
        {activeTab === "photos" && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Photos ({photosList.length})</CardTitle>
                <p className="text-sm text-slate-500">Drag photos to reorder. First photo is the main image.</p>
              </div>
            </CardHeader>
            <CardContent>
              {/* Upload Area */}
              <div
                {...getPhotoRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer mb-4 transition-colors ${
                  isPhotoDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <input {...getPhotoInputProps()} />
                {uploadingPhotos ? (
                  <Loader2 className="w-8 h-8 mx-auto text-blue-500 animate-spin" />
                ) : (
                  <>
                    <Camera className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Drag & drop photos here, or click to select multiple
                    </p>
                  </>
                )}
              </div>

              {/* Photo Grid with Drag & Drop */}
              {photosList.length > 0 && (
                <DragDropContext onDragEnd={handlePhotoDragEnd}>
                  <Droppable droppableId="photos" direction="horizontal">
                    {(provided) => (
                      <div 
                        {...provided.droppableProps} 
                        ref={provided.innerRef}
                        className="grid grid-cols-4 gap-4"
                      >
                        {photosList.map((photo, index) => (
                          <Draggable key={photo.filename} draggableId={photo.filename} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${
                                  snapshot.isDragging ? 'border-blue-500 shadow-lg' : 'border-transparent'
                                } ${index === 0 ? 'ring-2 ring-emerald-500' : ''}`}
                              >
                                <img
                                  src={`${API}/uploads/instructions/${listingId}/${photo.filename}`}
                                  alt={`Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = '';
                                    e.target.className = 'hidden';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
                                <div 
                                  {...provided.dragHandleProps}
                                  className="absolute top-2 left-2 p-1 bg-white rounded opacity-0 group-hover:opacity-100 cursor-grab"
                                >
                                  <GripVertical className="w-4 h-4" />
                                </div>
                                <button
                                  onClick={() => handleDeletePhoto(index)}
                                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                                {index === 0 && (
                                  <span className="absolute bottom-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded">
                                    Main
                                  </span>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "floorplans" && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Floorplans ({floorplansList.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                {...getFloorplanRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer mb-4 transition-colors ${
                  isFloorplanDragActive ? 'border-purple-500 bg-purple-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <input {...getFloorplanInputProps()} />
                {uploadingFloorplans ? (
                  <Loader2 className="w-8 h-8 mx-auto text-purple-500 animate-spin" />
                ) : (
                  <>
                    <Layout className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Drag & drop floorplans here, or click to select
                    </p>
                  </>
                )}
              </div>

              {floorplansList.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {floorplansList.map((fp, index) => (
                    <div key={fp.filename} className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden">
                      <img
                        src={`${API}/uploads/instructions/${listingId}/${fp.filename}`}
                        alt={`Floorplan ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "marketing" && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Marketing Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Property Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter a detailed property description..."
                  rows={8}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSaveDetails} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Description
              </Button>
            </CardContent>
          </Card>
        )}

        {activeTab === "features" && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Key Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="e.g. Double glazing throughout"
                  onKeyDown={(e) => e.key === "Enter" && handleAddFeature()}
                />
                <Button onClick={handleAddFeature} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {features.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No features added yet</p>
                ) : (
                  features.map((feature, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm">{feature}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveFeature(idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <Button onClick={handleSaveDetails} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Features
              </Button>
            </CardContent>
          </Card>
        )}

        {activeTab === "details" && (
          <>
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Bed className="w-6 h-6 mx-auto text-slate-600" />
                  <p className="text-2xl font-bold mt-2">{listing.bedrooms || 0}</p>
                  <p className="text-sm text-muted-foreground">Bedrooms</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Bath className="w-6 h-6 mx-auto text-slate-600" />
                  <p className="text-2xl font-bold mt-2">{listing.bathrooms || 0}</p>
                  <p className="text-sm text-muted-foreground">Bathrooms</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Home className="w-6 h-6 mx-auto text-slate-600" />
                  <p className="text-2xl font-bold mt-2">{listing.receptions || 0}</p>
                  <p className="text-sm text-muted-foreground">Receptions</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Building2 className="w-6 h-6 mx-auto text-slate-600" />
                  <p className="text-lg font-bold mt-2 capitalize">{listing.property_type?.replace("_", " ") || "-"}</p>
                  <p className="text-sm text-muted-foreground">Type</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Tenure</span>
                  <span className="font-medium capitalize">{listing.tenure || "-"}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">EPC Rating</span>
                  <span className="font-medium">{listing.epc_rating || "-"}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Council Tax Band</span>
                  <span className="font-medium">{listing.council_tax_band || "-"}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Agency Type</span>
                  <span className="font-medium capitalize">{listing.agency_type?.replace("_", " ") || "Sole"}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* EPC Section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                EPC Certificate
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>EPC Reference Number</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={epcReference}
                      onChange={(e) => setEpcReference(e.target.value)}
                      placeholder="e.g. 1234-5678-9012-3456-7890"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    20-digit reference from the EPC certificate
                  </p>
                </div>
                <div>
                  <Label>EPC Rating</Label>
                  <Select value={epcRating} onValueChange={setEpcRating}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      {EPC_RATINGS.map((rating) => (
                        <SelectItem key={rating} value={rating}>
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold ${
                              rating === 'A' ? 'bg-green-600' :
                              rating === 'B' ? 'bg-green-500' :
                              rating === 'C' ? 'bg-lime-500' :
                              rating === 'D' ? 'bg-yellow-500' :
                              rating === 'E' ? 'bg-orange-500' :
                              rating === 'F' ? 'bg-orange-600' :
                              'bg-red-600'
                            }`}>
                              {rating}
                            </div>
                            Band {rating}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Current EPC Display */}
              {(listing.epc_rating || listing.epc_reference) && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Current EPC Details</p>
                  <div className="flex items-center gap-4">
                    {listing.epc_rating && (
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold ${
                        listing.epc_rating === 'A' ? 'bg-green-600' :
                        listing.epc_rating === 'B' ? 'bg-green-500' :
                        listing.epc_rating === 'C' ? 'bg-lime-500' :
                        listing.epc_rating === 'D' ? 'bg-yellow-500' :
                        listing.epc_rating === 'E' ? 'bg-orange-500' :
                        listing.epc_rating === 'F' ? 'bg-orange-600' :
                        'bg-red-600'
                      }`}>
                        {listing.epc_rating}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">Band {listing.epc_rating || 'Not set'}</p>
                      {listing.epc_reference && (
                        <p className="text-sm text-muted-foreground">Ref: {listing.epc_reference}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleSaveEpc} disabled={savingEpc}>
                  {savingEpc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save EPC Details
                </Button>
                <Button variant="outline" onClick={openEpcRegister}>
                  <FileText className="w-4 h-4 mr-2" />
                  View on Gov.uk
                </Button>
              </div>
            </CardContent>
          </Card>
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Listing</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this listing for{" "}
              <span className="font-semibold">{listing?.address_line1}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteListing}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Book Viewing Modal */}
      <Dialog open={showViewingModal} onOpenChange={setShowViewingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book Viewing</DialogTitle>
            <DialogDescription>
              Schedule a viewing for {listing?.address_line1}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={viewingForm.date}
                  onChange={(e) => setViewingForm({ ...viewingForm, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={viewingForm.time}
                  onChange={(e) => setViewingForm({ ...viewingForm, time: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Attendee Name *</Label>
              <Input
                value={viewingForm.attendee_name}
                onChange={(e) => setViewingForm({ ...viewingForm, attendee_name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Attendee Phone</Label>
              <Input
                value={viewingForm.attendee_phone}
                onChange={(e) => setViewingForm({ ...viewingForm, attendee_phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Attendee Email</Label>
              <Input
                type="email"
                value={viewingForm.attendee_email}
                onChange={(e) => setViewingForm({ ...viewingForm, attendee_email: e.target.value })}
                placeholder="Email address"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={viewingForm.notes}
                onChange={(e) => setViewingForm({ ...viewingForm, notes: e.target.value })}
                placeholder="Any special requirements or notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewingModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleBookViewing} disabled={bookingViewing}>
              {bookingViewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
              Book Viewing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Property Email</DialogTitle>
            <DialogDescription>
              Send property details for {listing?.address_line1}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>To *</Label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                placeholder="recipient@example.com"
              />
            </div>
            <div>
              <Label>Subject *</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={8}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetailPage;
